/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { IEmployeeReferral } from '../../actions/get-employee-referrals-list.action';
import { wineClubGiftDatePicker } from '../../settings/app.settings';
import { CartUtilities } from '../../Utilities/cart-utils';
import { DeliveryType, WineClubDataStore } from '../wine-club-signup/wine-club-data';
import { ICheckoutClubInfoCollectorData } from './checkout-club-info-collector.data';
import { ICheckoutClubInfoCollectorProps } from './checkout-club-info-collector.props.autogenerated';
import { GiftData } from './types/gift-type';
import { MemberData } from './types/member-type';

export interface IHeaderEditData {
    [commerceKey: string]: string | undefined;
}
export interface ICheckoutClubInfoCollectorViewProps extends ICheckoutClubInfoCollectorProps<ICheckoutClubInfoCollectorData>, IModuleStateProps {
    forMember: boolean;
    displayError: [];
    displayOnly: boolean;
    allowEdits: boolean;
    collectedInfo: MemberData | GiftData;
    referral?: IEmployeeReferral;
    submitMember(memberInfo: MemberData): void;
    submitGift(giftInfo: GiftData): void;
    updateData(aggregate: unknown): void;
}

/**
 *
 * CheckoutClubInfoCollector component
 * @extends {React.PureComponent<ICheckoutClubInfoCollectorProps<ICheckoutClubInfoCollectorData>>}
 */
// @ts-ignore ?
@withModuleState
@observer
class CheckoutClubInfoCollector extends React.Component<ICheckoutClubInfoCollectorViewProps> {

    @observable private _editing: boolean = true;
    @observable private _displayOnly: boolean = false;
    @observable private _displayError: JSX.Element[] = [];

    private _store: WineClubDataStore;

    // since we don't know at what sort of customer they are until runtime, we
    // rely on the view to tell us while edits are made. we will know once
    // submit runs what sort of data is contained
    private clubAggregate!: MemberData | GiftData;

    // used to create temporary layer which updates state on successful submits only
    private tempAggregate!: MemberData | GiftData;

    private referral: IEmployeeReferral | null = null;

    // taken from the checkout-guest-profile autosetter. since you have to be
    // signed in for club this should always exist anyway
    private _email: string =
        (this.props.context &&
            this.props.context.request &&
            this.props.context.request.user &&
            this.props.context.request.user.isAuthenticated &&
            this.props.context.request.user.emailAddress) || '';

    constructor(props: ICheckoutClubInfoCollectorViewProps) {
        super(props);

        this._store = WineClubDataStore.getInstance(() => {
            this.setState({});
        });

        this._submitGift = this._submitGift.bind(this);
        this._submitMember = this._submitMember.bind(this);
        this._updateData = this._updateData.bind(this);
    }

    public componentDidMount(): void {
        const query = this.props.context.request.query;
        if(query?.refId) {
            const foundReferral = this.props.data.employeeReferralsList.result?.referralsList.find(referral => {
                return referral.StaffId === query.refId;
            });
            if(foundReferral) {
                this.referral = foundReferral;
            }
        }
        void this._initAsCheckoutModule();
    }

    public render(): JSX.Element | null {

        const store = this._store.get();

        return this.props.renderView({
            ...this.props,
            submitGift: this._submitGift,
            submitMember: this._submitMember,
            updateData: this._updateData,
            forMember: store.forMyself,
            allowEdits: this._editing,
            collectedInfo: this.clubAggregate,
            displayOnly: this._displayOnly,
            displayError: this._displayError,
            referral: this.referral
        });
    }

    private _updateData(aggregate: MemberData | GiftData): void {
        this.tempAggregate = aggregate;
    }

    private _deliveryPreferenceMap(type: DeliveryType): string {
        return {
            PICKUP: 'Pickup',
            PREMIUM: 'Premium delivery',
            STANDARD: 'Standard delivery'
        }[type];
    }

    private async _submitGift(giftInfo: GiftData): Promise<void> {
        // WC means 'wine club'
        this._editing = false;
        const store = this._store.get();
        const headerEdit: IHeaderEditData = {
            'WC First Name': giftInfo.firstName,
            'WC Last Name': giftInfo.lastName,
            'Gift recipient Email': giftInfo.email,
            'Gift recipient Phone Number': giftInfo.phoneNumber,
            'Gift membership end date': giftInfo.endDateSubmit,
            'WC signup': 'True',
            IsGiftedMembership: 'True',
            'Wine club delivery preference': this._deliveryPreferenceMap(store.deliveryType)
        };
        if(this.referral) {
            headerEdit['WC Referrer Id'] = this.referral.StaffId;
        }

        // pickup location id should exist here but lets just be safe about it
        if (store.deliveryType === 'PICKUP' && store.pickupLocationId) {
            headerEdit['Wine Club Pickup Location'] = store.pickupLocationId;
        }

        const checkoutState = await this.props.data.checkout;
        await CartUtilities.editCartHeadExtensionProps(
            checkoutState.checkoutCart,
            this.props.context,
            headerEdit
        );

        await checkoutState.updateGuestCheckoutEmail({ newGuestCheckoutEmail: this._email });

        this._editing = true;
    }
    private async _submitMember(memberInfo: MemberData): Promise<void> {
        // WC means 'wine club'
        this._editing = false;
        const store = this._store.get();
        const headerEdit: IHeaderEditData = {
            'WC First Name': memberInfo.firstName,
            'WC Last Name': memberInfo.lastName,
            'WC Additional Name': memberInfo.additionalMemberFirstName || '',
            'WC signup': 'True',
            IsGiftedMembership: 'False',
            'Wine club delivery preference': this._deliveryPreferenceMap(store.deliveryType)
        };
        if(this.referral) {
            headerEdit['WC Referrer Id'] = this.referral.StaffId;
        }
        // pickup location id should exist here but lets just be safe about it
        if (store.deliveryType === 'PICKUP' && store.pickupLocationId) {
            headerEdit['Wine Club Pickup Location'] = store.pickupLocationId;
        }
        const checkoutState = await this.props.data.checkout;
        await CartUtilities.editCartHeadExtensionProps(
            checkoutState.checkoutCart,
            this.props.context,
            headerEdit
        );

        await checkoutState.updateGuestCheckoutEmail({ newGuestCheckoutEmail: this._email });

        this._editing = true;
    }

    private async _initAsCheckoutModule(): Promise<void> {
        const cart = await this.props.data.cart;
        if (cart) {
            // the onUpdate, onReady, setHasError was copied from the
            // date of birth checkout implementation
            this.props.moduleState.init({
                onEdit: () => {
                    this._editing = true;
                    this._displayOnly = false;
                    this._displayError = [];
                    this.props.moduleState.onUpdating();
                },
                onCancel: () => {
                    // reset back to last saved state
                    this.tempAggregate = this.clubAggregate;
                    this.props.moduleState.setHasError(false);
                    this.props.moduleState.onReady();
                    this._displayOnly = true;
                    this._editing = false;
                    this._displayError = [];
                },
                onSubmit: async () => {
                    const { forMyself } = this._store.get();
                    if (this._formIsInvalid() && this._formIsInvalid().length) {
                        this._displayOnly = false;
                        this._displayError = this._formIsInvalid();
                        this.props.moduleState.setHasError(true);
                        this.props.moduleState.onUpdating();
                        return;
                    }
                    // set club form info state after validation passed
                    this.clubAggregate = this.tempAggregate;

                    if (forMyself) {
                        await this._submitMember(this.clubAggregate as MemberData)
                        .catch(e => {
                            this.props.moduleState.setHasError(true);
                            this._displayOnly = false;
                        });
                    } else {
                        await this._submitGift(this.clubAggregate as GiftData)
                        .catch(e => {
                            this.props.moduleState.setHasError(true);
                            this._displayOnly = false;
                        });
                    }
                    this.props.moduleState.setHasError(false);
                    this.props.moduleState.onReady();
                    this._displayOnly = true;
                },
                status: 'updating'
            });
        }
    }

    private _formIsInvalid(): JSX.Element[] {
        const aggregate: MemberData | GiftData = this.tempAggregate;
        const { forMyself } = this._store.get();
        const errors = [];

        // validation for gift ending date
        if (!forMyself) {
            const giftAggregate = aggregate as GiftData;
            const configDatePicker = this.props.app.config.wineClubGiftDatePicker as wineClubGiftDatePicker;
            if (giftAggregate.endDateSubmit) {
                const minimumDate = moment().add(configDatePicker.minType, configDatePicker.minLength || 0);
                const dateSelected = moment(giftAggregate.endDateSubmit);
                if (dateSelected && dateSelected.isBefore(minimumDate, 'days')) {
                    errors.push
                        (
                            <span className='checkout-club-info-collector-error-lines' role='alert' aria-live='assertive' aria-invalid='true' aria-describedby={this.props.resources.checkoutClubInfoCollector__endDateValidationError}>
                                {this.props.resources.checkoutClubInfoCollector__endDateValidationError}
                            </span>
                        );
                }
            }
        }

        if (aggregate.firstName === '' || aggregate.lastName === '') {
            errors.push
                (
                    <span className='checkout-club-info-collector-error-lines' role='alert' aria-live='assertive' aria-invalid='true' aria-describedby={this.props.resources.checkoutClubInfoCollector__firstAndLastValidationError}>
                        {this.props.resources.checkoutClubInfoCollector__firstAndLastValidationError}
                    </span>
                );
        }
        return errors;
    }
}

export default CheckoutClubInfoCollector;
